/**
 * Pure helpers extracted from ThemeContext.tsx so jest can verify the
 * theme-resolution + DOM-apply contract without mounting a React
 * ThemeProvider. ThemeContext.tsx re-imports these — production
 * behavior unchanged.
 */

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const STORAGE_KEY = 'eqiq-theme';

/**
 * Detect the OS-level color scheme via matchMedia. Defaults to 'dark'
 * during SSR (no window) — matches the brand's dark-first design.
 */
export function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

/**
 * Read the user's stored theme preference from localStorage. Defaults
 * to 'dark' if missing or malformed (brand-matching default that
 * survives a corrupted storage value).
 */
export function getStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'dark';
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored;
  }
  return 'dark';
}

/**
 * Apply the resolved theme to the DOM. Mutates two things:
 *   1. `<html>` class list — swaps 'light'/'dark' for Tailwind's
 *      dark: variant resolution
 *   2. `<meta name="theme-color">` content — drives the mobile browser
 *      chrome color (status bar, etc) so the OS UI matches the app
 */
export function applyTheme(resolvedTheme: ResolvedTheme): void {
  const root = document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(resolvedTheme);

  // Update meta theme-color for mobile browsers
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute(
      'content',
      resolvedTheme === 'dark' ? '#171717' : '#ffffff',
    );
  }
}
